import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ROUTES } from "@/shared/constants/routes";
import { Tab, Tabs } from "@nextui-org/react";
import clsx from "clsx";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AllThirdPartiesTab } from "./tabs/AllThirdPartiesTab";
import { BreachHistoryTab } from "./tabs/BreachHistoryTab";
import { OverviewTab } from "./tabs/OverviewTab";

const TPRADashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [totalVendors, setTotalVendors] = useState(0);
  const [isVendorsLoading, setIsVendorsLoading] = useState(true);

  const handleTabChange = (key: React.Key) => {
    setActiveTab(key as string);
  };

  const handleVendorCountChange = (count: number, isLoading: boolean) => {
    setTotalVendors(count);
    setIsVendorsLoading(isLoading);
  };

  const handleStartNewAssessment = () => {
    // Navigate to agent page with state to trigger modal
    navigate(`/${ROUTES.AGENT}`, {
      state: {
        openVendorAssessment: true,
        sessionTitle: "", // Can be pre-filled if needed
      },
    });
  };

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs items={[{ title: "TPRM Dashboard", href: `/${ROUTES.VENDORS}` }]} />
          </div>
          <ZButton color="primary" className="text-xs font-medium h-7 px-3" onClick={handleStartNewAssessment}>
            Start New Assessment
          </ZButton>
        </div>
        <div className="flex-1 overflow-hidden rounded-lg flex flex-col bg-white">
          <div className="p-4 flex flex-col gap-2">
            {/* header */}
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center flex-1 gap-2">
                <p className="text-sm font-medium leading-5 text-[#171717]">Third-Party Risk Management Dashboard</p>
              </div>
              <ZButton variant="bordered" className="h-6 px-3 text-xs font-medium">
                Download Report
              </ZButton>
            </div>
          </div>
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Tabs */}
            <div className="h-32px w-full border-b-1 border-[#E4E4E7]">
              <Tabs
                variant="underlined"
                selectedKey={activeTab}
                classNames={{
                  tab: "rounded-none px-4 text-[12px] text-[#18181B]",
                  base: "rounded-none",
                  cursor: "rounded-none",
                  panel: "rounded-none",
                  tabContent: "rounded-none",
                  tabList: "rounded-none p-0",
                  wrapper: "rounded-none",
                }}
                className="bg-white"
                onSelectionChange={handleTabChange}
              >
                <Tab
                  className={clsx("rounded-none", {
                    'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                      activeTab === "overview",
                  })}
                  key="overview"
                  title={
                    <div className="flex items-center gap-2">
                      <span className="text-[#18181B]">Overview</span>
                    </div>
                  }
                />
                <Tab
                  className={clsx("rounded-none", {
                    'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                      activeTab === "breach-history",
                  })}
                  key="breach-history"
                  title={
                    <div className="flex items-center gap-2">
                      <span className="text-[#18181B]">Breach History</span>
                    </div>
                  }
                />
                <Tab
                  className={clsx("rounded-none", {
                    'after:content-[""] after:absolute after:bottom-0 after:left-0 after:w-full after:h-full after:bg-gradient-to-t after:from-[#E7F2FF] after:to-[#FFFFFF] after:border-b-2 after:border-b-[#006FEE]':
                      activeTab === "all-third-parties",
                  })}
                  key="all-third-parties"
                  title={
                    <div className="flex items-center gap-2">
                      <span className="text-[#18181B]">All Third-Parties</span>
                    </div>
                  }
                />
              </Tabs>
            </div>

            {/* Content Area */}
            <div
              className={clsx(
                "flex-1 p-4 pb-0 flex flex-col",
                activeTab === "all-third-parties" ? "overflow-hidden" : "overflow-auto",
              )}
            >
              {activeTab === "overview" && (
                <div className="h-full flex flex-col">
                  <h2 className="font-inter text-[14px] font-medium leading-[20px] tracking-[0%] text-[#71717A] mb-4">
                    Overview
                  </h2>
                  <OverviewTab onNavigateToBreachHistory={() => setActiveTab("breach-history")} />
                </div>
              )}

              {activeTab === "breach-history" && (
                <div>
                  <h2 className="font-inter text-[14px] font-medium leading-[20px] tracking-[0%] text-[#71717A] mb-4">
                    Overview
                  </h2>
                  <BreachHistoryTab />
                </div>
              )}

              {activeTab === "all-third-parties" && (
                <div className="h-full flex flex-col overflow-hidden">
                  <div className="flex items-center gap-2 mb-4">
                    <h2 className="font-inter text-[14px] font-medium leading-[20px] tracking-[0%] text-[#71717A]">
                      All Third-Parties
                    </h2>
                    {!isVendorsLoading && (
                      <div className="p-1 bg-[#F4F4F5] px-2 rounded-lg">
                        <p className="text-[10px] text-[#52525B] leading-4 font-medium">{totalVendors}</p>
                      </div>
                      // <span className="px-2 py-0.5 bg-gray-200 rounded text-xs">{}</span>
                    )}
                  </div>
                  <AllThirdPartiesTab onVendorCountChange={handleVendorCountChange} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default TPRADashboard;
